import { yupResolver } from '@hookform/resolvers/yup'
import { Alert, Grid, Stack, Typography } from '@mui/material'
import AsyncActionButton from '@src/components/AsyncActionButton'
import { useGetMerchantFinancingProgramProviders, useGetMerchantMechanicalWarrantyProducts } from '@src/data/api/merchants-api/merchants-api'
import {
  useCreateMechanicalWarrantyWorksheet,
  useEditMechanicalWarrantyWorksheet,
} from '@src/data/api/worksheet-api/mechanical-warranty-worksheet-api'
import { reportErrorToConsole } from '@src/services/error-logger'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { buildMechanicalWarrantyWorksheetSchema, MechanicalWarrantyWorksheet } from '@src/types/WorksheetSchema'
import { useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import MechanicalWarrantyExtended from './components/MechanicalWarrantyExtended'
import MechanicalWarrantyWorksheetDetails from './components/MechanicalWarrantyWorksheetDetails'
import MechanicalWarrantyWorksheetLoanPaymentDetails from './components/MechanicalWarrantyWorksheetLoanPaymentDetails'
import { useMechanicalWarrantyCompute } from './hooks/useMechanicalWarrantyCompute'

interface Props {
  merchant: Merchant
  creditApplication: CreditApplication
}

const MechanicalWarrantyWorksheetPage = ({ merchant: _, creditApplication }: Props) => {
  const MechanicalWarrantyWorksheetSchema = buildMechanicalWarrantyWorksheetSchema(creditApplication)
  const [defaultValues] = useState<MechanicalWarrantyWorksheet>({
    ...(MechanicalWarrantyWorksheetSchema.getDefault() as unknown as MechanicalWarrantyWorksheet),
    ...creditApplication.worksheet,
  } as unknown as MechanicalWarrantyWorksheet)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [createWorksheet, isCreating, hasCreateError, createError] = useCreateMechanicalWarrantyWorksheet()
  const [editWorksheet, isEditting, hasEditError, editError] = useEditMechanicalWarrantyWorksheet()
  const [merchantMechanicalWarrantyProviders, isLoadingProviders] = useGetMerchantFinancingProgramProviders(creditApplication?.merchantId ?? '', creditApplication?.financingProgramId ?? '')

  const methods = useForm<MechanicalWarrantyWorksheet>({
    mode: 'onBlur',
    defaultValues,
    resolver: yupResolver(MechanicalWarrantyWorksheetSchema),
  })

  const selectedProviderId = useWatch({ control: methods.control, name: 'providerId' })
  const selectedProvider = merchantMechanicalWarrantyProviders?.find(p => p.id === selectedProviderId) ?? null

  const [merchantMechanicalWarrantyProducts, isLoadingProducts] = useGetMerchantMechanicalWarrantyProducts(creditApplication?.merchantId ?? '', selectedProviderId ?? '')

  const navigateToView = () => {
    navigate(`/credits/${creditApplication.financingProgramId}/${creditApplication.id}`, { replace: true })
  }

  const worksheet = useWatch({ control: methods.control }) as MechanicalWarrantyWorksheet

  const { computeDto, computedResult, isComputing } = useMechanicalWarrantyCompute({
    creditApplicationId: creditApplication.id,
    worksheet,
    merchantMechanicalWarrantyProvider: selectedProvider,
  })

  const onSubmit = async (data: MechanicalWarrantyWorksheet) => {
    data.creditApplicationId = creditApplication.id
    if (!isCreating && !isEditting) {
      if (creditApplication.worksheet) {
          await editWorksheet({ worksheet: data }).then(() => {
            navigateToView()})
      } else {

          await createWorksheet({ worksheet: data }).then(() => {
            navigateToView()})
      }
    }
  }

  if (!merchantMechanicalWarrantyProviders?.length || isLoadingProviders) {
    return <Grid />
  }

  return (
    <FormProvider {...methods}>
    <form onSubmit={methods.handleSubmit(onSubmit, reportErrorToConsole)}>
        {(createError || editError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography>{createError?.response?.data?.message || editError?.response?.data?.message}</Typography>
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid size={12}>
            <Stack direction="row" spacing={2}>
              <Typography flexGrow={1} variant="h4">
                {t('mechanicalWarrantyWorksheet.title')}
              </Typography>
              <Stack direction="row" spacing={1}>
                <AsyncActionButton fullWidth={false} variant="contained" href="..">
                  {t('common.previous')}
                </AsyncActionButton>

                <AsyncActionButton
                  fullWidth={false}
                  variant="contained"
                  type="submit"
                  disabled={isComputing || !computeDto}
                  isPending={isCreating || isEditting}
                  isError={hasCreateError || hasEditError}
                >
                  {t('worksheet.createContract')}
                </AsyncActionButton>
              </Stack>
            </Stack>
          </Grid>
          <Grid container spacing={2} alignItems="stretch">
            <Grid size={4}>
              <MechanicalWarrantyExtended
                merchantMechanicalWarrantyProviders={merchantMechanicalWarrantyProviders}
                merchantMechanicalWarrantyProducts={merchantMechanicalWarrantyProducts ?? []}
                isLoadingProducts={isLoadingProducts}
              />
            </Grid>
            <Grid size={8}>
              <Stack direction="column" spacing={2} sx={{ height: '100%' }}>
                <MechanicalWarrantyWorksheetDetails
                  creditApplication={creditApplication}
                  computedLoanAmounts={computedResult}
                  merchantMechanicalWarrantyProducts={merchantMechanicalWarrantyProducts ?? []}
                  isComputing={isComputing}
                />
                <MechanicalWarrantyWorksheetLoanPaymentDetails
                  computedLoanAmounts={computedResult}
                  isComputing={isComputing}
                />
              </Stack>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}

export default MechanicalWarrantyWorksheetPage
